<template>
    <div class="PageScroller" :style="{ transform: 'translateX(' + scrollX + 'px)' }" @pointerdown="handlePointerDown"
        @wheel.passive="handleScrollWheel">
        <slot></slot>

    </div>
    <!-- <div class="PageScrollState">{{ state }}</div> -->
    <div class="PageScrollbar" :style="{ width: windowWidth + 'px' }" v-show="scrollbarVisible">
        <div class="handle" :style="{ transform: 'translateX(' + scrollbarPosition + 'px)', width: scrollbarWidth + 'px' }">
        </div>
    </div>
</template>

<script>

export const scrollState = Object.freeze({
    STATIONARY: 'Stationary',
    DRAGGING: 'Dragging',
    THROWING: 'Throwing',
    ANIMATING: 'Animating',
});


export default {
    props: ["enabled", "startPosition"],

    data() {
        return {

            // master scroll 
            scrollX: 0,

            // sizing 
            globalScale: null,
            backgroundWidth: null,
            windowWidth: null,
            scrollMinMax: null,
            initializing: true,

            // auto scrolling
            state: scrollState.STATIONARY,

            // scrollbar 
            scrollbarPosition: 0,
            scrollbarWidth: 0,
            scrollbarVisible: false,

        }
    },

    mounted() {

        if (!this.enabled)
            return;

        // handle stuff when resizing
        window.addEventListener("backgroundLoaded", this.onRezize);

        // handle stuff when resizing
        window.addEventListener("resize", this.onRezize);

        // local scroll vars
        let offsetX = 0;
        let clientX = 0;
        let animationFrameOccupied = false;
        let previousScrollX = 0;
        let throwSpeed = 0;
        let throwDamping = .95;


        /// POINTER EVENTS ///
        this.handlePointerDown = (event) => {
            // no scrolling when: 
            // 1) window is wider than background 
            // 2) clicking something other than leftclick
            if (this.scrollMinMax <= 0 || event.button !== 0) {
                return;
            }

            //console.log(event.pointerType, event.clientX);
            event.preventDefault();
            this.$store.commit('toggleDraggingBackground', true)

            // drag offset position
            offsetX = event.clientX / this.globalScale - this.scrollX;

            // start draggin (stop animating)
            this.state = scrollState.DRAGGING;

            const handlePointerMove = (event) => {
                clientX = event.clientX / this.globalScale;
                if (!animationFrameOccupied) {
                    animationFrameOccupied = true;
                    requestAnimationFrame(calcScrollX);
                }
            };

            const handlePointerUp = () => {
                window.removeEventListener('pointermove', handlePointerMove);
                window.removeEventListener('pointerup', handlePointerUp);
                this.$store.commit('toggleDraggingBackground', false);

                // throw!
                startThrowAnimation();
            };

            window.addEventListener('pointermove', handlePointerMove);
            window.addEventListener('pointerup', handlePointerUp);
        };


        /// CALCULATE X WHILE DRAGGING ///
        const calcScrollX = () => {

            // calculate new drag distance
            let dragDelta = clientX - offsetX;
            let clampedDelta = this.clampXToMinMax(dragDelta);
            let overDrag = (dragDelta - clampedDelta) * 0.15;
            this.scrollX = clampedDelta + overDrag;

            // meassuring current speed (if thrown)
            throwSpeed = this.scrollX - previousScrollX;
            previousScrollX = this.scrollX;

            // ready for next drag event
            animationFrameOccupied = false;

            this.updateScrollbar();

        }

        /// CALCULATE X AFTER THROW ///
        const animateThrow = () => {

            if (this.state === scrollState.THROWING) {

                var absoluteThrowSpeed = Math.abs(throwSpeed)
                if (absoluteThrowSpeed > 0.1) {
                    // degrade throwspeed
                    throwSpeed = throwSpeed * throwDamping;
                } else {
                    throwSpeed = 0;
                }

                // Update the scroll position
                this.scrollX += throwSpeed;

                this.updateScrollbar()

                // slow down if border crossed
                if (outOfLimits()) {
                    throwDamping = 0.2;
                }


                if (absoluteThrowSpeed > 0) {
                    // Continue animating until throwspeed is degraded completely
                    requestAnimationFrame(animateThrow);

                } else {
                    // check if limits are crossed
                    var limitsCrossed = outOfLimits();

                    if (limitsCrossed && limitsCrossed != 0) {

                        // duration between 200ms and 400ms depending on how far limit is crossed
                        var durationMs = Math.min(400, Math.max(Math.abs(limitsCrossed) + 200, 200));

                        // go back to limit
                        if (limitsCrossed < 0) {
                            this.animateToX(-this.scrollMinMax, durationMs, this.quadOut);
                        } else {
                            this.animateToX(this.scrollMinMax, durationMs, this.quadOut);
                        }
                    } else {
                        // done throwing - no limits crossed
                        this.state = scrollState.STATIONARY;
                    }
                }
            }
        };

        const startThrowAnimation = () => {
            // reset damping
            throwDamping = 0.95;
            // start the animation if not already animating
            if (this.state != scrollState.THROWING) {
                this.state = scrollState.THROWING
                requestAnimationFrame(animateThrow);
            }
        };

        const outOfLimits = () => {
            if (this.scrollX < -this.scrollMinMax) {
                // start limit crossed
                return (this.scrollX + this.scrollMinMax)
            }
            if (this.scrollX > this.scrollMinMax) {
                // end limit crossed
                return (this.scrollX - this.scrollMinMax);
            }

            return null;
        }


        // other interaction events

        this.handleScrollWheel = (event) => {
            this.scrollX = this.clampXToMinMax(this.scrollX -= event.deltaY);;
            this.updateScrollbar();
        }

    },



    methods: {

        onRezize() {
            // refresh size props
            this.getSizeProps();

            requestAnimationFrame(() => {

                if (this.initializing) {

                    // goto given start position
                    this.goto(this.startPosition, 0, "linear");
                    this.initializing = false;

                } else {

                    // update current scroll position compared to new size
                    var clampedNewX = 0;
                    if (this.scrollMinMax > 0) {
                        var clampedNewX = this.clampXToMinMax(this.scrollX)
                    }
                    this.animateToX(clampedNewX, 0);

                }
            });
        },

        getSizeProps() {
            this.globalScale = this.$store.getters.getScaleValue;
            this.backgroundWidth = this.$store.getters.getBackgroundWidth;
            this.windowWidth = window.innerWidth / this.globalScale;
            this.scrollMinMax = (this.backgroundWidth - this.windowWidth) / 2;
            this.updateScrollbar();


            /* console.table({
                "globalScale": this.globalScale,
                "backgroundWidth": this.backgroundWidth,
                "windowWidth": this.windowWidth,
                "scrollMinMax": this.scrollMinMax,
            }); */

            // if still nulls try resize again
            if (!this.globalScale || !this.backgroundWidth) {
                setTimeout(() => {
                    this.onRezize();
                }, 200);
            }

        },

        // calculate scrollbar position, size and visiblility
        updateScrollbar() {
            var viewPortPct = this.windowWidth / this.backgroundWidth;
            this.scrollbarPosition = - this.scrollX * viewPortPct;
            this.scrollbarWidth = viewPortPct * this.windowWidth;
            this.scrollbarVisible = (viewPortPct < 1 && this.enabled);
        },

        // GOTO takes any fraction, percentage or position name as strings or floats (fractions)
        // e.g. ("0.33" / "33.3%" / "start","end","center")
        // calculates the x-position and animates towards it 
        goto(position, duration, easing = "easeInOut") {
            var positionFraction = this.getFractionFromAny(position);
            var positionX = ((positionFraction * 2 - 1) * -this.scrollMinMax)
            this.animateToX(positionX, duration, easing);
        },

        // the basic animator - used directily when the x position is at hand
        animateToX(xPos = 0, duration = 100, easing = "linear") {

            this.state = scrollState.ANIMATING

            const startTime = performance.now();
            const startX = this.scrollX;

            const easingMethod = this.getEasing(easing);

            const animate = (currentTime) => {
                const elapsedTime = currentTime - startTime;
                if (elapsedTime >= duration) {
                    this.scrollX = xPos;
                    this.updateScrollbar();
                    this.state = scrollState.STATIONARY;
                    return;
                }

                const progress = easingMethod(elapsedTime / duration);
                this.scrollX = startX + (xPos - startX) * progress;
                this.updateScrollbar();

                if (this.state === scrollState.ANIMATING)
                    requestAnimationFrame(animate);
            };

            requestAnimationFrame((currentTime) => {
                animate(currentTime);
            });
        },



        // goto helpers //

        getFractionFromAny(position) {

            if (typeof position === "string") {
                // strings
                if (position.includes("%")) {
                    // percentage
                    position = position.replace("%", "");
                    return (parseFloat(position / 100))
                } else if (position.startsWith("0") || position.startsWith("1")) {
                    // fraction
                    return (parseFloat(position))
                } else {
                    // name
                    switch (position) {
                        case "start":
                            return 0;
                        case "center":
                            return 0.5;
                        case "end":
                            return 1;
                        default:
                            return 0;
                    }
                }

            } else {
                // int or float
                return position;
            }
        },

        clampXToMinMax(xPos) {
            return (Math.max(-this.scrollMinMax, Math.min(this.scrollMinMax, xPos)));
        },


        // easings (maybe to be placed in a global utillity) //

        getEasing(easingString) {

            switch (easingString) {
                case "quadInOut":
                case "easeInOut":
                default:
                    return this.quadInOut;
                case "quadOut":
                case "easeOut":
                    return this.quadOut;
                case "linear":
                    return this.linear;

            }

        },

        quadInOut(progress) {
            progress *= 2;
            if (progress < 1) {
                return 0.5 * progress * progress;
            } else {
                progress -= 1;
                return -0.5 * (progress * (progress - 2) - 1);
            }
        },
        quadOut(progress) {
            return 1 - Math.pow(1 - progress, 2);
        },
        linear(progress) {
            return progress;
        },

    },
    unmounted() {
        window.removeEventListener("backgroundLoaded", this.onRezize);
        window.removeEventListener("resize", this.onRezize);
    }
};


</script>


<style lang="scss">
.PageScroller {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
    touch-action: auto;


}

.PageScrollbar {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 10px;
    bottom: 0px;
    background-color: black;
    z-index: 4;

    .handle {
        position: absolute;
        height: 10px;

        background-color: var(--main-color);
        z-index: 5;
    }
}

.PageScrollState {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 10px;
    z-index: 4;
    color: #fff;
    font-size: 100px;
}
</style>